<template>
    <div v-if="selectedItem">
        <div>
            <h6 class="font-weight-bold no-wrap align-items-center mb-0" v-text="layerTypeTitle" />
            <modal-import-list-item :item="selectedItem" :prevent-action="true" />
        </div>
        <div class="row mt-4 align-items-center">
            <div class="col-12 col-sm-7">
                <h6 class="font-weight-bold no-wrap align-items-center">Select the layers to import</h6>
            </div>
            <div class="col-12 col-sm-5">
                <modal-import-search @search-text-change="updateSearchText" />
            </div>
        </div>
        <modal-import-layers-tree class="my-2" :tree-item="treeItem" />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import store from '@/store';
import { TreeItem, TreeType, TemplateProduct, EPS, LayerType } from '../../../types';
import { CanvasElement, CanvasElementType } from '@/typings/customilyElements/canvasElements';
import { getComputedElementType } from '@/utils/elementsUtils';
import ModalImportListItem from '../../list/ModalImportListItem.vue';
import ModalImportSearch from '../../search/ModalImportSearch.vue';
import ModalImportLayersTree from '../../layers-tree/ModalImportLayersTree.vue';

const props = defineProps<{
    type: LayerType;
    rawElement: TemplateProduct;
}>();

const searchText = ref('');

const selectedItem = computed(() => store.state.storeLayersModal!.selectedItem);

const layerTypeTitle = computed(() => (props.type === LayerType.DESIGN ? 'Design' : 'Template'));

const treeItem = computed(() => {
    const item = getTreeItems();
    const epsLength = getEpsLength(item);

    if (epsLength <= 1 && item.children) {
        hideEps(item.children);
    }

    return item;
});

const hideEps = (children: TreeItem[]) => {
    for (const child of children || []) {
        if (child.type !== TreeType.PAGE_EPS) continue;
        child.type = TreeType.HIDDEN;
    }
};

const getEpsLength = (treeItem: TreeItem) => {
    if (treeItem.type === TreeType.SELECT_ALL && treeItem.children) {
        return treeItem.children.filter((child) => child.type === TreeType.PAGE_EPS).length;
    }
    return 0;
};

const getTreeItems = (): TreeItem => {
    const template = props.rawElement;

    return {
        children: template.listEPS.map((eps) => createEpsTreeItem(eps, template)),
        name: 'Select All',
        icon: '',
        type: TreeType.SELECT_ALL
    };
};

const createEpsTreeItem = (eps: EPS, template: TemplateProduct): TreeItem => {
    const groups = template.groups.slice();
    const groupsIds = getGroupsIdsFromEps(eps);

    groups.sort((a, b) => b.zIndex - a.zIndex);

    let children: TreeItem[] = [];

    if (groups.length) {
        children = groups
            .filter((group) => groupsIds.includes(group.id))
            .map(
                (group) =>
                    ({
                        id: group.id,
                        icon: 'group',
                        name: group.name,
                        children: getCanvasElementsByEps({
                            eps,
                            groupId: group.id
                        }),
                        type: TreeType.GROUP
                    } as TreeItem)
            );
    }

    children = [
        ...children,
        ...getCanvasElementsByEps({
            eps
        })
    ];

    return {
        id: eps.id,
        uuid: eps.EpsId,
        icon: 'page',
        name: eps.name,
        children,
        type: TreeType.PAGE_EPS
    };
};

const getCanvasElementsByEps = (data: { eps: EPS; groupId?: string }) => {
    const textsEpsElements = extractCanvasElements(data.eps.textsEps, 'bounded-text', data.groupId);
    const circularTextsEpsElements = extractCanvasElements(data.eps.circularTextsEps, 'path-itext', data.groupId);
    const imagePlaceHoldersEpsElements = extractCanvasElements(
        data.eps.imagePlaceHoldersEps,
        'image-placeholder',
        data.groupId
    );
    const vectorEPSEpsElements = extractCanvasElements(data.eps.vectorEPSEps, 'vector-placeholder', data.groupId);

    const orderedCanvasElements = [
        ...textsEpsElements,
        ...circularTextsEpsElements,
        ...imagePlaceHoldersEpsElements,
        ...vectorEPSEpsElements
    ].sort((a, b) => {
        if (!(a.zIndex && b.zIndex)) {
            return 0;
        }
        return b.zIndex - a.zIndex;
    });

    return orderedCanvasElements;
};

const getGroupsIdsFromEps = (eps: EPS) => {
    const textEpsGroups = eps.textsEps.map((item) => item.groupId);
    const circularTextsEpsGroups = eps.circularTextsEps.map((item) => item.groupId);
    const imagePlaceHoldersEpsGroups = eps.imagePlaceHoldersEps.map((item) => item.groupId);
    const vectorEPSEpsGroups = eps.vectorEPSEps.map((item) => item.groupId);

    const groupsIds = [
        ...textEpsGroups,
        ...circularTextsEpsGroups,
        ...imagePlaceHoldersEpsGroups,
        ...vectorEPSEpsGroups
    ];
    return groupsIds;
};

const updateSearchText = (value: string) => {
    searchText.value = value;
};

const extractCanvasElements = (
    items: CanvasElement[],
    elementType: CanvasElementType,
    groupId?: string
): TreeItem[] => {
    const filteredItems = groupId
        ? items.filter((item) => item.groupId === groupId)
        : items.filter((item) => !item.groupId);

    const _searchText = searchText.value.toLowerCase();

    const filteredBySearch = _searchText
        ? filteredItems.filter((item) => item.name?.toLowerCase().includes(_searchText))
        : filteredItems;

    return filteredBySearch
        .sort((a, b) => a.zIndex - b.zIndex)
        .map((item) => ({
            id: item.id,
            uuid: item.uuid,
            icon: getComputedElementType(item, elementType),
            name: item.name,
            type: TreeType.CANVAS_ELEMENT,
            zIndex: item.zIndex
        }));
};
</script>
